import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { AuthService } from '../auth/auth.service'
import { ProfileService } from '../profile/profile.service'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { OnboardingService } from '../../chatstyle/onboarding/onboarding.service';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {

  constructor(private authService: AuthService,
    private profileService: ProfileService,
    private router: Router,
    private oidcSecurityService: OidcSecurityService,
    private onboardingService: OnboardingService) {
}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    console.log(state.url);

    // Use checkAuth() to restore session from storage before checking auth status.
    // This prevents the guard from redirecting to login on page refresh when valid tokens exist.
    return this.oidcSecurityService.checkAuth().pipe(
      switchMap(({ isAuthenticated }) => {
        if (isAuthenticated) {
          return this.handleLoggedInUser(route, state);
        }

        this.authService.startAuthentication(state);
        return of(false);
      })
    );
  }

  private handleLoggedInUser(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const failurl = '/';

    if ("claims" in route.data) {
      const claims = route.data.claims as Array<string>;
      const userClaims = this.authService.getClaims();

      for (let index = 0; index < claims.length; ++index) {
        if (claims[index] in userClaims) {
          if ("value" in route.data) {
            if (userClaims[claims[index]] !== route.data.value) {
              this.router.navigateByUrl(failurl);
              return of(false);
            }
          }
          return of(true);
        }
      }

      this.router.navigateByUrl(failurl);
      return of(false);
    }

    if ("privilege" in route.data) {
      // Load the profile before checking privileges (refresh-safe — DEFAULT_PROFILE
      // has empty privileges so the check would otherwise fail before the real
      // profile lands).
      return this.profileService.load().pipe(
        switchMap(() => {
          const priv = route.data.privilege;
          const isSetupUrl = state.url.includes('/setup/');

          // Active users are past onboarding by definition — profile.status === 'active' is the
          // chat-tenant-DB signal that the wizard finalized. Skip the /onboarding/state round-trip
          // entirely (it would read _Tenants.ChatProvisioningStates, staging data we want to be
          // free to prune).
          if (this.profileService.isActive()) {
            return of(this.checkPrivilegesSync(priv));
          }

          // Non-active users: ask the server. shouldRedirectToLaunchpad encapsulates the full
          // decision (SuperAdmin, bot state, cold-start staging row).
          return this.onboardingService.getOnboardingState().pipe(
            switchMap(model => {
              // Fail safe: only enter the wizard on an EXPLICIT negative from the server. The old
              // `!model?.shouldRedirectToLaunchpad` also fired when model was null/undefined, so an
              // ambiguous or empty response was enough to trap an established tenant in onboarding
              // — which is how a fully onboarded admin ended up at step 1 on every login (CHAT-877).
              const sendToWizard = model?.shouldRedirectToLaunchpad === false;
              if (sendToWizard && !isSetupUrl) {
                this.router.navigateByUrl('/setup/setupwizard/step1');
                return of(false);
              }
              return of(this.checkPrivilegesSync(priv));
            }),
            catchError(err => {
              console.error('AuthGuard - Error loading onboarding state:', err);
              // On error, fall back to the privilege check — fail open for established users.
              return of(this.checkPrivilegesSync(priv));
            })
          );
        }),
        catchError(() => of(false))
      );
    }

    return of(true);
  }

  /**
   * Helper method to check if user has required privilege
   */
  private checkPrivilegesSync(priv: string): boolean {
    // '/' itself requires 'User', so an 'Onboarding'-only user sent there fails this check again
    // and loops forever (CHAT-1089). Step 1 settles it either way: it resumes the wizard, or its
    // redirectToLaunchpad() swaps 'Onboarding' for 'User' before going to '/'.
    const failurl = this.profileService.isOnboarding() ? '/setup/setupwizard/step1' : '/';

    if (this.profileService.profile && this.profileService.profile.privileges) {
      const userPrivs = this.profileService.profile.privileges;

      if (userPrivs !== null && userPrivs.indexOf(priv) >= 0) {
        return true;
      }
    }

    this.router.navigateByUrl(failurl);
    return false;
  }
}
